import { download, downloadFile } from 'services/http/http';

/**
 * Download one or many SSCC labels, each as its own file.
 *
 * The bytes are streamed by the authenticated `/transport-labels/{name}/download` endpoint (not the
 * raw S3 URL), so the request goes through the app's credentialed axios instance. `downloadFile`
 * fetches to a blob and saves it via a same-origin object URL, which forces a real download without
 * navigating the page or opening a tab — and, being blob-based, a bulk download isn't stopped by the
 * popup blocker.
 */
export const downloadSsccLabels = async (pdfUrls: Array<string>): Promise<void> => {
  for (const url of pdfUrls) {
    const name = attachmentNameFromUrl(url);
    await downloadFile(labelDownloadPath(url), { filename: name });
  }
};

/**
 * Print one or many SSCC labels as a single job (one label page per printed page).
 *
 * The label bytes are fetched from the authenticated endpoint through the credentialed axios
 * instance, then handed to the pdfjs printer. That printer is pulled in lazily: it configures its
 * worker via an `import.meta` URL, and this table is rendered by many Jest suites whose ts-jest
 * (`isolatedModules`) transform can't parse `import.meta`. Loading it only when the user actually
 * prints keeps it out of those suites.
 */
export const printSsccLabels = async (pdfUrls: Array<string>): Promise<void> => {
  if (!pdfUrls.length) {
    return;
  }
  const blobs = await Promise.all(pdfUrls.map((url) => download(labelDownloadPath(url)).then(({ data }) => data)));
  const { printPdfsFromBlobs } = await import('services/pdf/printPdfs');
  await printPdfsFromBlobs(blobs);
};

const attachmentNameFromUrl = (url: string): string => {
  const name = url.split('/').pop()?.split('?')[0];
  if (!name) {
    throw new Error(`Could not derive a transport-label name from "${url}"`);
  }
  return name;
};

// The label file is streamed by the authenticated endpoint keyed by its attachment name, which is
// the filename carried in the unit's `pdf_url`.
const labelDownloadPath = (pdfUrl: string): string =>
  `/v1/transactions/transport-labels/${encodeURIComponent(attachmentNameFromUrl(pdfUrl))}/download`;
